import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Archive,
  ArchiveRestore,
  ArrowLeftRight,
  Banknote,
  Check,
  CreditCard,
  Plus,
  Tags,
  Trash2,
  Vault,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';

import PeriodPicker from '@/components/PeriodPicker';
import Sheet from '@/components/Sheet';
import { apiDelete, apiErrorMessage, apiGet, apiPatch, apiPost, newIdempotencyKey } from '@/lib/api';
import { formatKop, parseRubToKop } from '@/lib/format';
import { moscowToday, periodLabel, recallPeriod, rememberPeriod, type PeriodValue } from '@/lib/period';
import {
  METHOD_BAR,
  METHOD_LABEL,
  METHOD_TONE,
  SALES_METHODS,
  type ExpenseCategory,
  type ExpensesResponse,
  type SalesExpense,
  type SalesMethod,
} from '@/lib/sales';

const METHOD_ICON: Record<SalesMethod, LucideIcon> = {
  CASH: Banknote,
  CARD: CreditCard,
  TRANSFER: ArrowLeftRight,
  SAFE: Vault,
};

const DAY_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', weekday: 'short', timeZone: 'Europe/Moscow' });
const MSK_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' });

function dayKey(iso: string): string {
  return MSK_DATE.format(new Date(iso));
}

function dayTitle(key: string): string {
  const today = moscowToday();
  const yesterday = MSK_DATE.format(new Date(Date.now() - 24 * 60 * 60 * 1000));
  if (key === today) return 'Сегодня';
  if (key === yesterday) return 'Вчера';
  const s = DAY_FMT.format(new Date(`${key}T12:00:00+03:00`));
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type MethodFilter = SalesMethod | 'all';

export default function Expenses() {
  const [period, setPeriodState] = useState<PeriodValue>(recallPeriod);
  const [method, setMethod] = useState<MethodFilter>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [data, setData] = useState<ExpensesResponse | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState<SalesExpense | 'new' | null>(null);
  const [managingCategories, setManagingCategories] = useState(false);

  const setPeriod = (v: PeriodValue) => {
    rememberPeriod(v);
    setPeriodState(v);
  };

  const loadCategories = useCallback(() => {
    void apiGet<ExpenseCategory[]>('/director/expense-categories', { silent: true })
      .then(setCategories)
      .catch(() => undefined);
  }, []);

  useEffect(loadCategories, [loadCategories]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ from: period.from, to: period.to });
    if (method !== 'all') params.set('method', method);
    if (categoryId !== 'all') params.set('categoryId', categoryId);
    void apiGet<ExpensesResponse>(`/director/expenses?${params.toString()}`, { silent: true })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Не удалось загрузить расходы'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, method, categoryId, version]);

  const groups = useMemo(() => {
    const out: Array<{ key: string; items: SalesExpense[]; total: number }> = [];
    for (const e of data?.items ?? []) {
      const key = dayKey(e.spentAt);
      const last = out[out.length - 1];
      if (last?.key === key) {
        last.items.push(e);
        last.total += e.amountKopecks;
      } else {
        out.push({ key, items: [e], total: e.amountKopecks });
      }
    }
    return out;
  }, [data]);

  const filtered = method !== 'all' || categoryId !== 'all';
  const totals = data?.totals;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Расходы отдела</h1>
          <p className="mt-1 text-sm text-ink/55">{periodLabel(period.from, period.to)}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn border border-line bg-white text-ink/80 hover:bg-paper" onClick={() => setManagingCategories(true)}>
            <Tags size={16} aria-hidden /> Статьи
          </button>
          <button type="button" className="btn-primary flex-1 sm:flex-none" onClick={() => setEditing('new')}>
            <Plus size={16} aria-hidden /> Добавить расход
          </button>
        </div>
      </div>

      <PeriodPicker value={period} onChange={setPeriod} />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 sm:mx-0 sm:px-0" role="group" aria-label="Способ оплаты">
          {(['all', ...SALES_METHODS] as MethodFilter[]).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={method === m}
              onClick={() => setMethod(m)}
              className={clsx(
                'shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium min-h-[36px] transition-colors',
                method === m ? 'bg-orange/10 text-orange-700' : 'text-ink/60 hover:bg-ink/5',
              )}
            >
              {m === 'all' ? 'Все способы' : METHOD_LABEL[m]}
            </button>
          ))}
        </div>
        <select
          className="input sm:ml-auto sm:!w-56 !py-2"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          aria-label="Статья"
        >
          <option value="all">Все статьи</option>
          <option value="none">Без статьи</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.isActive ? '' : ' (в архиве)'}
            </option>
          ))}
        </select>
      </div>

      {error ? <div className="card p-4 text-sm text-ink/70">{error}</div> : null}

      {totals ? (
        <section className={clsx('card p-5 transition-opacity', loading && 'opacity-60')}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm text-ink/55">{filtered ? 'Итого по фильтру' : 'Итого за период'}</h2>
            <div className="text-2xl font-semibold tabular-nums">{formatKop(totals.totalKopecks)}</div>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {SALES_METHODS.map((m) => (
              <div key={m} className="rounded-lg bg-paper px-3 py-2.5">
                <dt className="flex items-center gap-1.5 text-xs text-ink/55">
                  <span aria-hidden className={clsx('h-2 w-2 rounded-full', METHOD_BAR[m])} />
                  {METHOD_LABEL[m]}
                </dt>
                <dd className="mt-0.5 font-semibold tabular-nums">{formatKop(totals.byMethod[m])}</dd>
              </div>
            ))}
          </dl>
          {totals.byCategory.length > 0 ? (
            <ul className="mt-4 space-y-2 border-t border-line pt-4">
              {totals.byCategory.map((c) => (
                <li key={c.categoryId ?? 'none'} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 text-sm">
                  <span className="truncate text-ink/70">{c.name}</span>
                  <span className="font-medium tabular-nums">{formatKop(c.kopecks)}</span>
                  <div className="col-span-2 mt-1 h-1.5 overflow-hidden rounded-full bg-line/70">
                    <div
                      className="h-full rounded-full bg-ink/70"
                      style={{ width: `${totals.totalKopecks > 0 ? (c.kopecks / totals.totalKopecks) * 100 : 0}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : loading ? (
        <div className="text-sm text-ink/50">Загрузка…</div>
      ) : null}

      {data && data.items.length === 0 ? (
        <div className="card flex flex-col items-start gap-3 p-6 text-sm text-ink/60">
          {filtered ? 'По этому фильтру расходов нет.' : 'За этот период расходов нет.'}
          {!filtered ? (
            <button type="button" className="btn-primary" onClick={() => setEditing('new')}>
              <Plus size={16} aria-hidden /> Добавить первый
            </button>
          ) : null}
        </div>
      ) : null}

      {groups.map((g) => (
        <section key={g.key} className="card overflow-hidden">
          <div className="flex items-baseline justify-between border-b border-line px-4 py-2.5 text-sm">
            <span className="font-medium">{dayTitle(g.key)}</span>
            <span className="tabular-nums text-ink/55">{formatKop(g.total)}</span>
          </div>
          <ul className="divide-y divide-line/60">
            {g.items.map((e) => {
              const Icon = METHOD_ICON[e.method];
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-paper focus-visible:bg-paper focus-visible:outline-none"
                    onClick={() => setEditing(e)}
                  >
                    <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', METHOD_TONE[e.method])}>
                      <Icon size={17} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{e.category?.name ?? 'Без статьи'}</span>
                      <span className="block truncate text-xs text-ink/50">
                        {METHOD_LABEL[e.method]}
                        {e.comment ? ` · ${e.comment}` : ''}
                        {e.createdBy ? ` · ${e.createdBy.name}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums">{formatKop(e.amountKopecks)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {editing ? (
        <ExpenseSheet
          expense={editing === 'new' ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}

      {managingCategories ? (
        <CategoriesSheet
          categories={categories}
          onClose={() => setManagingCategories(false)}
          onChanged={() => {
            loadCategories();
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function ExpenseSheet({
  expense,
  categories,
  onClose,
  onSaved,
}: {
  expense: SalesExpense | null;
  categories: ExpenseCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState(expense ? String(expense.amountKopecks / 100).replace('.', ',') : '');
  const [method, setMethod] = useState<SalesMethod>(expense?.method ?? 'CASH');
  const [categoryId, setCategoryId] = useState<string>(expense?.category?.id ?? '');
  const [date, setDate] = useState(expense ? dayKey(expense.spentAt) : moscowToday());
  const [comment, setComment] = useState(expense?.comment ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [idemKey] = useState(newIdempotencyKey);

  const selectable = categories.filter((c) => c.isActive || c.id === categoryId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const kop = parseRubToKop(amount);
    if (kop === null || kop < 100) {
      setError('Укажите сумму от 1 ₽');
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      amountKopecks: kop,
      method,
      categoryId: categoryId || null,
      comment: comment.trim() || null,
      ...(!expense || date !== dayKey(expense.spentAt) ? { spentAt: date } : {}),
    };
    try {
      if (expense) await apiPatch(`/director/expenses/${expense.id}`, body, { silent: true });
      else await apiPost('/director/expenses', body, { silent: true, idempotencyKey: idemKey });
      toast.success(expense ? 'Расход сохранён' : 'Расход добавлен');
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось сохранить расход'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!expense || busy) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    try {
      await apiDelete(`/director/expenses/${expense.id}`, { silent: true });
      toast.success('Расход удалён');
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось удалить расход'));
      setBusy(false);
    }
  };

  return (
    <Sheet title={expense ? 'Расход' : 'Новый расход'} onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <label className="label" htmlFor="exp-amount">Сумма, ₽</label>
          <input
            id="exp-amount"
            className="input text-lg font-semibold tabular-nums"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus={!expense}
            required
          />
        </div>

        <fieldset>
          <legend className="label">Чем оплатили</legend>
          <div className="grid grid-cols-2 gap-2">
            {SALES_METHODS.map((m) => {
              const Icon = METHOD_ICON[m];
              const active = method === m;
              return (
                <button
                  key={m}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setMethod(m)}
                  className={clsx(
                    'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium min-h-[44px] transition-colors',
                    active ? 'border-orange bg-orange/10 text-ink' : 'border-line text-ink/70 hover:border-ink/30',
                  )}
                >
                  <Icon size={16} aria-hidden className={active ? 'text-orange' : 'text-ink/40'} />
                  {METHOD_LABEL[m]}
                </button>
              );
            })}
          </div>
          {method === 'SAFE' ? (
            <p className="mt-2 text-xs text-ink/55">Сумма уменьшит остаток сейфа отдела.</p>
          ) : null}
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="exp-category">Статья</label>
            <select id="exp-category" className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Без статьи</option>
              {selectable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="exp-date">Дата</label>
            <input
              id="exp-date"
              type="date"
              className="input"
              value={date}
              max={moscowToday()}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              required
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="exp-comment">Комментарий</label>
          <textarea
            id="exp-comment"
            className="input min-h-[72px] resize-y"
            maxLength={500}
            placeholder="За что и кому"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        {expense?.createdBy ? (
          <p className="text-xs text-ink/45">Добавил {expense.createdBy.name}</p>
        ) : null}

        {error ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

        <div className={clsx('grid gap-2', expense && 'grid-cols-[auto_1fr]')}>
          {expense ? (
            <button
              type="button"
              className={clsx(
                'btn border',
                confirmDelete ? 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700' : 'border-line text-rose-700 hover:bg-rose-50',
              )}
              onClick={() => void onDelete()}
              disabled={busy}
            >
              <Trash2 size={16} aria-hidden />
              {confirmDelete ? 'Удалить?' : ''}
              <span className="sr-only">{confirmDelete ? '' : 'Удалить расход'}</span>
            </button>
          ) : null}
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            <Check size={16} aria-hidden />
            {busy ? 'Сохраняем…' : expense ? 'Сохранить' : 'Добавить'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function CategoriesSheet({
  categories,
  onClose,
  onChanged,
}: {
  categories: ExpenseCategory[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || name.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      await apiPost('/director/expense-categories', { name: name.trim() }, { silent: true });
      setName('');
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось добавить статью'));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (c: ExpenseCategory, body: { name?: string; isActive?: boolean }) => {
    setError(null);
    try {
      await apiPatch(`/director/expense-categories/${c.id}`, body, { silent: true });
      setDrafts((d) => {
        const next = { ...d };
        delete next[c.id];
        return next;
      });
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось изменить статью'));
    }
  };

  const commitName = (c: ExpenseCategory) => {
    const draft = drafts[c.id]?.trim();
    if (draft && draft !== c.name && draft.length >= 2) void patch(c, { name: draft });
  };

  return (
    <Sheet title="Статьи расходов" onClose={onClose}>
      <div className="space-y-4">
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {categories.map((c) => (
            <li key={c.id} className="flex items-center gap-2 px-2 py-1.5">
              <input
                className={clsx(
                  'min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1.5 text-base focus:border-line focus:bg-white focus:outline-none',
                  !c.isActive && 'text-ink/40 line-through',
                )}
                value={drafts[c.id] ?? c.name}
                maxLength={60}
                aria-label={`Название статьи ${c.name}`}
                onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                onBlur={() => commitName(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
              />
              <button
                type="button"
                className="btn-ghost !min-h-[40px] !px-2.5 text-xs"
                onClick={() => void patch(c, { isActive: !c.isActive })}
                title={c.isActive ? 'Убрать в архив' : 'Вернуть из архива'}
              >
                {c.isActive ? <Archive size={16} aria-hidden /> : <ArchiveRestore size={16} aria-hidden />}
                <span className="hidden sm:inline">{c.isActive ? 'В архив' : 'Вернуть'}</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink/50">
          Статья в архиве пропадает из формы расхода, а прошлые расходы по ней остаются в отчёте.
        </p>
        <form className="flex gap-2" onSubmit={onAdd}>
          <input
            className="input"
            placeholder="Новая статья"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            aria-label="Название новой статьи"
          />
          <button type="submit" className="btn-primary shrink-0" disabled={busy || name.trim().length < 2}>
            <Plus size={16} aria-hidden /> Добавить
          </button>
        </form>
        {error ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
      </div>
    </Sheet>
  );
}
